/**
 * Regional Environment Definitions
 *
 * Defines the available regions and their fallback configurations.
 * Adapted from the browser extension implementation.
 */
/**
 * Available regional environments
 */
export const REGIONAL_ENVIRONMENTS = [
    {
        region: 'na',
        name: 'North America',
        appUrl: 'https://airia.ai',
        fallbackApiHost: 'api.airia.ai',
        fallbackAuthHost: 'identity.airia.ai',
        fallbackOfficeUrl: 'https://office.airia.ai',
    },
    {
        region: 'eu',
        name: 'Europe',
        appUrl: 'https://eu1.airia.ai',
        fallbackApiHost: 'eu1.api.airia.ai',
        fallbackAuthHost: 'eu1.identity.airia.ai',
        fallbackOfficeUrl: 'https://eu1.office.airia.ai',
    },
    {
        region: 'au',
        name: 'Australia',
        appUrl: 'https://prodaus.airia.ai',
        fallbackApiHost: 'prodaus.api.airia.ai',
        fallbackAuthHost: 'prodaus.identity.airia.ai',
        fallbackOfficeUrl: 'https://prodaus.office.airia.ai',
    },
    {
        region: 'mena',
        name: 'MENA',
        appUrl: 'https://mena.airia.ai',
        fallbackApiHost: 'mena.api.airia.ai',
        fallbackAuthHost: 'mena.identity.airia.ai',
        fallbackOfficeUrl: 'https://mena.office.airia.ai',
    },
    {
        region: 'sg',
        name: 'Singapore',
        appUrl: 'https://sg01.airia.ai',
        fallbackApiHost: 'sg01.api.airia.ai',
        fallbackAuthHost: 'sg01.identity.airia.ai',
        fallbackOfficeUrl: 'https://sg01.office.airia.ai',
    },
];
/**
 * Get a regional environment by region code
 */
export function getRegionalEnvironment(region) {
    return REGIONAL_ENVIRONMENTS.find((env) => env.region === region);
}
/**
 * Get the default regional environment (North America)
 */
export function getDefaultRegionalEnvironment() {
    return REGIONAL_ENVIRONMENTS[0];
}
/**
 * Service subdomains used in Airia's URL structure
 */
export const SERVICE_SUBDOMAINS = ['office', 'api', 'identity', 'chat', 'community'];
/**
 * Check if a URL contains a service subdomain (office, api, identity, etc.)
 *
 * @param url - The URL to check
 * @returns true if the URL contains a service subdomain
 */
export function isServiceUrl(url) {
    try {
        const parsed = new URL(url);
        const parts = parsed.hostname.split('.');
        return parts.some((part) => SERVICE_SUBDOMAINS.includes(part));
    }
    catch {
        return false;
    }
}
/**
 * Create a fallback config for a custom URL.
 *
 * This is a BEST-EFFORT fallback used only when service discovery fails.
 * Custom deployments should configure their `.well-known/service-configuration`
 * endpoint to return the correct URLs.
 *
 * ## Supported URL Patterns
 *
 * The inference assumes Airia's standard subdomain naming convention:
 *
 * | Input URL | Inferred API | Inferred Identity | Inferred Office |
 * |-----------|--------------|-------------------|-----------------|
 * | `https://tenant.domain.com` | `tenant.api.domain.com` | `tenant.identity.domain.com` | `tenant.office.domain.com` |
 * | `https://domain.com` | `api.domain.com` | `identity.domain.com` | `office.domain.com` |
 * | `https://sub.tenant.domain.com` | `sub.tenant.api.domain.com` | `sub.tenant.identity.domain.com` | `sub.tenant.office.domain.com` |
 * | `https://tenant.office.domain.com` | `tenant.api.domain.com` | `tenant.identity.domain.com` | `tenant.office.domain.com` |
 *
 * ## Limitations
 *
 * - Does NOT handle `airia.tenant.com` patterns (tenant as TLD subdomain)
 * - Does NOT handle non-standard port configurations
 * - Does NOT handle path-based routing (e.g., `domain.com/api`)
 * - Falls back to NA region URLs if parsing fails entirely
 *
 * ## Recommendation
 *
 * Custom deployments should NOT rely on this inference. Instead, configure
 * the `/.well-known/service-configuration` endpoint on the frontend URL
 * to return the correct `api`, `identity-provider`, and `office` rels.
 *
 * @param appUrl - The frontend app URL entered by the user
 * @returns Inferred fallback hosts (best-effort, may be incorrect)
 */
export function createCustomFallbackConfig(appUrl) {
    try {
        const url = new URL(appUrl);
        const hostname = url.hostname;
        const parts = hostname.split('.');
        // Handle standard patterns:
        // - tenant.domain.com -> tenant.api.domain.com, tenant.identity.domain.com
        // - domain.com -> api.domain.com, identity.domain.com
        // - sub.tenant.domain.com -> sub.tenant.api.domain.com, etc.
        // - tenant.office.domain.com -> tenant.api.domain.com (detect office URLs)
        if (parts.length >= 2) {
            const baseDomain = parts.slice(-2).join('.');
            let prefix = parts.length > 2 ? parts.slice(0, -2).join('.') + '.' : '';
            // Check if the URL is already an office/api/identity URL and extract the tenant prefix
            // e.g., "dev.office.airiadev.ai" -> prefix should be "dev." not "dev.office."
            const serviceSubdomains = ['office', 'api', 'identity', 'chat', 'community'];
            for (const service of serviceSubdomains) {
                const serviceWithDot = `${service}.`;
                if (prefix.includes(serviceWithDot)) {
                    prefix = prefix.replace(serviceWithDot, '');
                    break;
                }
            }
            return {
                fallbackApiHost: `${prefix}api.${baseDomain}`,
                fallbackAuthHost: `${prefix}identity.${baseDomain}`,
                fallbackOfficeUrl: `${url.protocol}//${prefix}office.${baseDomain}`,
            };
        }
        // Fallback for unusual URLs (single-part hostnames, etc.)
        return {
            fallbackApiHost: `api.${hostname}`,
            fallbackAuthHost: `identity.${hostname}`,
            fallbackOfficeUrl: `${url.protocol}//office.${hostname}`,
        };
    }
    catch {
        // If URL parsing fails entirely, return NA region as ultimate fallback
        console.warn('[Regions] Failed to parse custom URL, using NA region fallback');
        return {
            fallbackApiHost: 'api.airia.ai',
            fallbackAuthHost: 'identity.airia.ai',
            fallbackOfficeUrl: 'https://office.airia.ai',
        };
    }
}
